import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import TopBar from '../components/TopBar';
import MetaLabel from '../components/MetaLabel';
import CrisisRail from '../components/CrisisRail';
import BreathingModal from '../components/BreathingModal';
import GroundingModal from '../components/GroundingModal';
import { tokens } from '../tokens';
import useIsDesktop from '../useIsDesktop';
import api from '../api';

/* ─── Hardcoded toolkit data from prototype ─── */
const TOOLKIT_DATA = {
  crisis: [
    { name: '988 Suicide & Crisis Lifeline',
      line: 'Call or text 988. 24/7. Free. Confidential.',
      action: 'tel:988', actionLabel: 'Call 988',
      alt: 'sms:988', altLabel: 'Text 988',
      tag: 'US' },
    { name: 'Crisis Text Line',
      line: 'Text HOME to 741741. A trained counselor will answer, usually within minutes.',
      action: 'sms:741741&body=HOME', actionLabel: 'Text HOME to 741741',
      tag: 'US \u00b7 CA \u00b7 UK \u00b7 IE' },
    { name: 'The Trevor Project',
      line: '24/7 crisis support for LGBTQ+ young people under 25.',
      action: 'tel:18664887386', actionLabel: 'Call 1-866-488-7386',
      alt: 'sms:678678&body=START', altLabel: 'Text START to 678-678',
      tag: 'LGBTQ+' },
    { name: 'Trans Lifeline',
      line: 'Trans peer support hotline. Run by and for trans people.',
      action: 'tel:18775658860', actionLabel: 'Call 1-877-565-8860',
      tag: 'Trans \u00b7 US \u00b7 CA' },
  ],

  groups: [
    { id: 'industry', title: 'Industry mental health',
      blurb: 'People who know the work.',
      items: [
        { name: 'Entertainment Community Fund',
          line: 'Counseling, social services, and financial aid for entertainment professionals. Free, confidential.',
          action: 'https://entertainmentcommunity.org',
          tags: ['Therapy', 'Financial aid'] },
        { name: 'Behind the Scenes (BTS)',
          line: 'Support for entertainment technology pros facing illness, injury, or death in family. Grants and mental health initiative.',
          action: 'https://btshelp.org',
          tags: ['Crew', 'Grants'] },
        { name: 'Backup Trust (UK)',
          line: 'Financial and emotional aid for UK entertainment crew.',
          action: 'https://backuptrust.org.uk',
          tags: ['UK'] },
      ] },

    { id: 'unions', title: 'Union EAPs',
      blurb: 'Your union probably covers this already.',
      items: [
        { name: 'Actors\' Equity Wellness',
          line: 'Free therapist finder, member assistance, and wellness programming.',
          action: 'https://actorsequity.org/benefits/wellness',
          tags: ['AEA'] },
        { name: 'SAG-AFTRA Foundation',
          line: 'Emergency assistance, scholarships, and wellness programs for eligible members.',
          action: 'https://sagaftra.foundation',
          tags: ['SAG-AFTRA'] },
      ] },

    { id: 'therapy', title: 'Affordable therapy',
      blurb: 'Sliding scale and low-cost directories.',
      items: [
        { name: 'Open Path Collective',
          line: '$40\u201380/session with vetted therapists. One-time membership fee.',
          action: 'https://openpathcollective.org',
          tags: ['Sliding scale'] },
        { name: 'Inclusive Therapists',
          line: 'Directory centering marginalized communities. Filter by identity, insurance, specialty.',
          action: 'https://inclusivetherapists.com',
          tags: ['BIPOC', 'LGBTQ+', 'Sliding scale'] },
        { name: 'Psychology Today',
          line: 'Largest therapist directory. Filter by insurance, fee, and specialty.',
          action: 'https://psychologytoday.com/us/therapists',
          tags: ['Directory'] },
      ] },

    { id: 'financial', title: 'Financial aid & grants',
      blurb: 'Emergency help when the gig ends or the body gives out.',
      items: [
        { name: 'Entertainment Community Fund \u2014 Emergency Aid',
          line: 'Short-term grants for entertainment workers facing a crisis.',
          action: 'https://entertainmentcommunity.org/social-services',
          tags: ['Grants'] },
        { name: 'Musicians Foundation',
          line: 'Emergency assistance for professional musicians of any genre.',
          action: 'https://musiciansfoundation.org',
          tags: ['Musicians'] },
        { name: 'Theatre Cares (Broadway Cares)',
          line: 'Grants and support for theatre workers facing critical illness.',
          action: 'https://broadwaycares.org',
          tags: ['Theatre'] },
      ] },

    { id: 'legal', title: 'Legal aid',
      blurb: 'For harassment, wage theft, visas, and contract disputes.',
      items: [
        { name: 'Volunteer Lawyers for the Arts',
          line: 'Free/low-cost legal services for artists and arts orgs \u2014 contracts, IP, immigration.',
          action: 'https://vlany.org',
          tags: ['Arts law'] },
        { name: 'Workplace Fairness',
          line: 'Plain-language guide to your workplace rights. Find a worker-rights attorney.',
          action: 'https://workplacefairness.org',
          tags: ['Workplace'] },
        { name: 'RAINN (harassment / assault)',
          line: '24/7 hotline and online chat. Legal navigators available.',
          action: 'tel:18006564673', actionLabel: 'Call 1-800-656-HOPE',
          tags: ['Harassment', 'Assault'] },
      ] },

    { id: 'recovery', title: 'Substance recovery',
      blurb: 'Industry-aware recovery support.',
      items: [
        { name: 'The Actors Fund Dayle McClintock Institute',
          line: 'Addiction services designed for entertainment professionals.',
          action: 'https://entertainmentcommunity.org/looking-help/addiction-recovery',
          tags: ['Industry'] },
        { name: 'SAMHSA National Helpline',
          line: '24/7 treatment referral for substance use and mental health. Free, confidential.',
          action: 'tel:18006624357', actionLabel: 'Call 1-800-662-HELP',
          tags: ['Referral'] },
        { name: 'In The Rooms',
          line: 'Free online recovery meetings, 24/7. Many industry-specific.',
          action: 'https://intherooms.com',
          tags: ['Meetings'] },
      ] },

    { id: 'peer', title: 'Peer support',
      blurb: 'The people in the trenches with you.',
      items: [
        { name: 'The Ghostlight Project',
          line: 'Artist-led initiative for inclusion in American theatre.',
          action: 'https://theghostlightproject.com',
          tags: ['Community'] },
        { name: 'Black Theatre Coalition',
          line: 'Advocacy and support for Black theatre professionals.',
          action: 'https://blacktheatrecoalition.org',
          tags: ['BIPOC'] },
      ] },

    { id: 'sleep', title: 'When you can\'t sleep',
      blurb: 'Because load-out was brutal and the hotel is loud.',
      items: [
        { name: 'Ghost Hours (right here)',
          line: 'Anonymous 1:1 chat with someone else up late. End-to-end encrypted.',
          action: '__go:ghost-hours',
          tags: ['In-app'] },
        { name: 'Insight Timer \u2014 free sleep library',
          line: 'Thousands of free guided sleep tracks and sound baths.',
          action: 'https://insighttimer.com',
          tags: ['Free'] },
      ] },
  ],
};

/* ─── Small SVG glyphs for resource items ─── */
function PhoneGlyph({ size = 12, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
      <path d="M4 5.5C4 4.7 4.7 4 5.5 4H8l2 5-2.5 1.5a11 11 0 005 5L14 13l5 2v2.5c0 .8-.7 1.5-1.5 1.5A13.5 13.5 0 014 5.5z"
        stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

function MessageGlyph({ size = 12, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
      <path d="M4 5h16v11H8l-4 3V5z"
        stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

/* ─── Right Now row ─── */
function RightNowRow({ label, sub, onClick, last = false }) {
  return (
    <button className="gl-btn-bare" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '16px 14px',
      borderBottom: last ? 'none' : `1px solid ${tokens.edge}`,
      width: '100%',
      transition: `background 200ms ${tokens.ease}`,
    }}
      onMouseEnter={e => e.currentTarget.style.background = tokens.accentGlow}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <div style={{ flex: 1 }}>
        <div style={{
          fontFamily: tokens.fontDisplay, fontSize: 16,
          color: tokens.textHi, lineHeight: 1.25,
          letterSpacing: tokens.displayTracking,
        }}>{label}</div>
        <div style={{
          fontFamily: tokens.fontBody, fontSize: 12.5,
          color: tokens.textLow, marginTop: 3,
        }}>{sub}</div>
      </div>
      <span style={{
        fontFamily: tokens.fontMono, fontSize: 15,
        color: tokens.textLow,
      }}>&rarr;</span>
    </button>
  );
}

/* ─── Accordion group ─── */
function AccordionGroup({ group, open, onToggle, onItemAction }) {
  return (
    <div style={{ borderBottom: `1px solid ${tokens.edge}` }}>
      <button className="gl-btn-bare" onClick={onToggle} style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '18px 2px',
      }}>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{
            fontFamily: tokens.fontDisplay, fontSize: 19,
            color: tokens.textHi, letterSpacing: tokens.displayTracking,
            lineHeight: 1.2,
          }}>{group.title}</div>
          <div style={{
            fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 13, color: tokens.textLow, marginTop: 3,
          }}>{group.blurb}</div>
        </div>
        <span style={{
          fontFamily: tokens.fontBody, fontSize: 11,
          color: tokens.textDim, letterSpacing: '0.05em',
        }}>{group.items.length}</span>
        <span style={{
          fontFamily: tokens.fontMono, fontSize: 18, color: tokens.textMid,
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          transition: `transform 300ms ${tokens.ease}`,
          display: 'inline-block', width: 16, textAlign: 'center',
        }}>+</span>
      </button>
      {open && (
        <div style={{ paddingBottom: 14, animation: `gl-fade-up 300ms ${tokens.ease}` }}>
          {group.items.map((item, i) => (
            <ResourceItem key={i} item={item} onAction={() => onItemAction(item)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Resource item ─── */
function ResourceItem({ item, onAction }) {
  const isTel = item.action?.startsWith('tel:');
  const isSms = item.action?.startsWith('sms:');
  const isGo  = item.action?.startsWith('__go:');
  const isUrl = !isTel && !isSms && !isGo;

  const primaryLabel = item.actionLabel ||
    (isUrl ? 'Visit site \u2192' : isGo ? 'Open \u2192' : 'Contact');

  return (
    <div style={{
      padding: '14px 14px 16px',
      marginTop: 6,
      background: tokens.wing,
      border: `1px solid ${tokens.edge}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: tokens.fontDisplay, fontSize: 15.5,
            color: tokens.textHi, letterSpacing: tokens.displayTracking,
            lineHeight: 1.3,
          }}>{item.name}</div>
        </div>
        {item.tags && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '50%' }}>
            {item.tags.map(t => (
              <span key={t} style={{
                padding: '2px 7px',
                fontFamily: tokens.fontBody, fontSize: 10,
                letterSpacing: '0.06em',
                color: tokens.textLow,
                border: `1px solid ${tokens.edge}`,
              }}>{t}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{
        marginTop: 8,
        fontFamily: tokens.fontBody, fontSize: 13,
        color: tokens.textMid, lineHeight: 1.55,
      }}>{item.line}</div>
      <div style={{ marginTop: 12 }}>
        {isGo ? (
          <button className="gl-btn-bare" onClick={onAction} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: tokens.accent, fontFamily: tokens.fontBody, fontSize: 13,
            borderBottom: `1px solid ${tokens.accent}55`, paddingBottom: 2,
          }}>{primaryLabel}</button>
        ) : (
          <a href={item.action} target={isUrl ? '_blank' : undefined} rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: tokens.accent, fontFamily: tokens.fontBody, fontSize: 13,
            borderBottom: `1px solid ${tokens.accent}55`, paddingBottom: 2,
            textDecoration: 'none',
          }}>
            {isTel && <PhoneGlyph />}
            {isSms && <MessageGlyph />}
            {primaryLabel}
          </a>
        )}
      </div>
    </div>
  );
}

/* ─── Admin resource form (preserved from original) ─── */
const inputStyle = {
  display: 'block', width: '100%', background: 'rgba(26,18,16,0.5)',
  border: `1px solid ${tokens.edge}`, borderRadius: 0,
  padding: '10px 16px', color: tokens.textHi, fontSize: 14,
  fontFamily: tokens.fontBody, outline: 'none', boxSizing: 'border-box',
};

function AdminResourceForm({ onDone }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', category: '', description: '', url: '', phone: '', pinned: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/resources').then(res => {
      setCategories(res.data.categories || []);
    }).catch(() => {});
  }, []);

  const resetForm = () => { setForm({ title: '', category: '', description: '', url: '', phone: '', pinned: false }); setEditingId(null); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title || !form.category) { setError('Title and category required'); return; }
    setSubmitting(true);
    try {
      if (editingId) { await api.put(`/resources/${editingId}`, form); } else { await api.post('/resources', form); }
      resetForm();
      if (onDone) onDone();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{
      background: tokens.wing, border: `1px solid ${tokens.edge}`,
      padding: 20, marginBottom: 24,
    }}>
      <MetaLabel style={{ marginBottom: 14 }}>Add Resource</MetaLabel>
      {error && <div style={{ color: '#d4908a', fontFamily: tokens.fontBody, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <input type="text" placeholder="Title" required value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} />
        <select required value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
          <option value="">Category...</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <textarea placeholder="Description" value={form.description}
        onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
        style={{ ...inputStyle, resize: 'none', marginBottom: 12 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <input type="text" placeholder="URL" value={form.url}
          onChange={e => setForm(f => ({ ...f, url: e.target.value }))} style={inputStyle} />
        <input type="text" placeholder="Phone" value={form.phone}
          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={inputStyle} />
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => { resetForm(); if (onDone) onDone(); }} className="gl-btn-bare"
          style={{ fontFamily: tokens.fontBody, fontSize: 13, color: tokens.textLow, padding: '6px 12px' }}>Cancel</button>
        <button type="submit" disabled={submitting} style={{
          fontFamily: tokens.fontBody, fontSize: 13, fontWeight: 500,
          color: tokens.ground, background: tokens.accent,
          padding: '8px 18px', border: 'none', cursor: 'pointer',
          opacity: submitting ? 0.5 : 1,
        }}>{submitting ? 'Saving...' : 'Add'}</button>
      </div>
    </form>
  );
}

/* ─── Desktop Crisis Grid — 4 cards side by side ─── */
function DesktopCrisisGrid({ items }) {
  return (
    <section style={{
      border: `1px solid ${tokens.accent}55`,
      background: tokens.accentGlow,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px',
        borderBottom: `1px solid ${tokens.accent}33`,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: tokens.accent,
          boxShadow: `0 0 8px ${tokens.accent}`,
          animation: 'gl-breathe 2.5s ease-in-out infinite',
        }} />
        <span style={{
          fontFamily: tokens.fontBody, fontSize: 11, letterSpacing: '0.18em',
          fontWeight: 500, textTransform: 'uppercase', color: tokens.accent,
        }}>If you need someone right now</span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
      }}>
        {items.map((item, i) => (
          <div key={item.name} style={{
            padding: '20px 20px',
            borderRight: i < items.length - 1 ? `1px solid ${tokens.accent}22` : 'none',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              fontFamily: tokens.fontMono, fontSize: 9, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: tokens.textLow, marginBottom: 10,
            }}>{item.tag}</div>
            <div style={{
              fontFamily: tokens.fontDisplay, fontSize: 18,
              fontWeight: tokens.displayWeight, color: tokens.textHi,
              letterSpacing: tokens.displayTracking, lineHeight: 1.25,
            }}>{item.name}</div>
            <div style={{
              marginTop: 8, fontFamily: tokens.fontBody, fontSize: 12.5,
              color: tokens.textMid, lineHeight: 1.55, flex: 1,
            }}>{item.line}</div>
            <div style={{ marginTop: 14, display: 'grid', gridTemplateRows: '1fr 1fr', gap: 6, minHeight: 84 }}>
              <a href={item.action} style={{
                textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '9px 12px', background: tokens.accent, color: tokens.ground,
                fontFamily: tokens.fontBody, fontSize: 12.5, fontWeight: 500,
                letterSpacing: '0.02em', justifyContent: 'center',
              }}>
                {item.action.startsWith('tel:') ? <PhoneGlyph /> : item.action.startsWith('sms:') ? <MessageGlyph /> : null}
                {item.actionLabel}
              </a>
              {item.alt ? (
                <a href={item.alt} style={{
                  textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '9px 12px', border: `1px solid ${tokens.accent}77`,
                  color: tokens.accent, fontFamily: tokens.fontBody, fontSize: 12.5,
                  fontWeight: 400, justifyContent: 'center',
                }}>
                  {item.alt.startsWith('tel:') ? <PhoneGlyph /> : item.alt.startsWith('sms:') ? <MessageGlyph /> : null}
                  {item.altLabel}
                </a>
              ) : (
                <span aria-hidden style={{ display: 'block' }} />
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Desktop Right Now Card ─── */
function DesktopRightNowCard({ label, sub, cta, onClick }) {
  return (
    <button className="gl-btn-bare" onClick={onClick} style={{
      display: 'flex', flexDirection: 'column',
      padding: '20px 20px',
      border: `1px solid ${tokens.edge}`,
      background: tokens.wing,
      transition: `border-color 200ms ${tokens.ease}`,
      textAlign: 'left',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = tokens.accent + '55'}
      onMouseLeave={e => e.currentTarget.style.borderColor = tokens.edge}
    >
      <div style={{
        fontFamily: tokens.fontDisplay, fontSize: 17,
        color: tokens.textHi, lineHeight: 1.3,
        letterSpacing: tokens.displayTracking,
      }}>{label}</div>
      <div style={{
        marginTop: 8, fontFamily: tokens.fontBody, fontSize: 12.5,
        color: tokens.textMid, lineHeight: 1.55, flex: 1,
      }}>{sub}</div>
      <div style={{
        marginTop: 16,
        fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: tokens.accent,
      }}>{cta} →</div>
    </button>
  );
}

/* ─── Desktop Resource Group — accordion that reveals card grid ─── */
function DesktopResourceGroup({ group, open, onToggle, onItemAction }) {
  return (
    <div style={{ borderBottom: `1px solid ${tokens.edge}` }}>
      <button className="gl-btn-bare" onClick={onToggle} style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '18px 2px',
      }}>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{
            fontFamily: tokens.fontDisplay, fontSize: 19,
            color: tokens.textHi, letterSpacing: tokens.displayTracking,
            lineHeight: 1.2,
          }}>{group.title}</div>
          <div style={{
            fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 13, color: tokens.textLow, marginTop: 3,
          }}>{group.blurb}</div>
        </div>
        <span style={{
          fontFamily: tokens.fontBody, fontSize: 11,
          color: tokens.textDim, letterSpacing: '0.05em',
        }}>{group.items.length}</span>
        <span style={{
          fontFamily: tokens.fontMono, fontSize: 18, color: tokens.textMid,
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          transition: `transform 300ms ${tokens.ease}`,
          display: 'inline-block', width: 16, textAlign: 'center',
        }}>+</span>
      </button>
      {open && (
        <div style={{
          paddingBottom: 18,
          animation: `gl-fade-up 300ms ${tokens.ease}`,
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(group.items.length, 4)}, 1fr)`,
            border: `1px solid ${tokens.edge}`,
          }}>
            {group.items.map((item, i) => {
              const isTel = item.action?.startsWith('tel:');
              const isSms = item.action?.startsWith('sms:');
              const isGo  = item.action?.startsWith('__go:');
              const isUrl = !isTel && !isSms && !isGo;
              const primaryLabel = item.actionLabel || (isUrl ? 'Visit site →' : isGo ? 'Open →' : 'Contact');
              return (
                <div key={i} style={{
                  padding: '20px 20px',
                  borderRight: i < group.items.length - 1 ? `1px solid ${tokens.edge}` : 'none',
                  display: 'flex', flexDirection: 'column',
                  background: tokens.wing,
                }}>
                  {item.tags && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {item.tags.map(t => (
                        <span key={t} style={{
                          padding: '2px 7px',
                          fontFamily: tokens.fontBody, fontSize: 9, letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: tokens.textLow, border: `1px solid ${tokens.edge}`,
                        }}>{t}</span>
                      ))}
                    </div>
                  )}
                  <div style={{
                    fontFamily: tokens.fontDisplay, fontSize: 17,
                    fontWeight: tokens.displayWeight, color: tokens.textHi,
                    letterSpacing: tokens.displayTracking, lineHeight: 1.25,
                  }}>{item.name}</div>
                  <div style={{
                    marginTop: 8, fontFamily: tokens.fontBody, fontSize: 12.5,
                    color: tokens.textMid, lineHeight: 1.55, flex: 1,
                  }}>{item.line}</div>
                  <div style={{ marginTop: 14 }}>
                    {isGo ? (
                      <button className="gl-btn-bare" onClick={() => onItemAction(item)} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '9px 12px', background: tokens.accent, color: tokens.ground,
                        fontFamily: tokens.fontBody, fontSize: 12.5, fontWeight: 500,
                        justifyContent: 'center', width: '100%',
                      }}>{primaryLabel}</button>
                    ) : (
                      <a href={item.action} target={isUrl ? '_blank' : undefined} rel="noopener noreferrer" style={{
                        textDecoration: 'none',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '9px 12px',
                        border: `1px solid ${tokens.accent}77`,
                        color: tokens.accent, fontFamily: tokens.fontBody, fontSize: 12.5,
                        justifyContent: 'center', width: '100%', boxSizing: 'border-box',
                      }}>
                        {isTel && <PhoneGlyph />}
                        {isSms && <MessageGlyph />}
                        {primaryLabel}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main Toolkit page ─── */
export default function Toolkit() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const [openGroup, setOpenGroup] = useState(null);
  const [exercise, setExercise] = useState(null);
  const [showAdminForm, setShowAdminForm] = useState(false);

  const handleItemAction = (item) => {
    if (item.action?.startsWith('__go:')) {
      navigate(`/${item.action.slice(5)}`);
    }
  };

  const rightNowSection = (
    <div>
      <MetaLabel tokens={tokens} style={{ marginBottom: 14 }}>Right now</MetaLabel>
      <div style={{ border: `1px solid ${tokens.edge}` }}>
        <RightNowRow label="I can't catch my breath" sub="60-second guided breathing" onClick={() => setExercise('breath')} />
        <RightNowRow label="I need to come back to my body" sub="5-4-3-2-1 grounding" onClick={() => setExercise('ground')} />
        <RightNowRow label="I can't sleep and I'm alone" sub="Step into Ghost Hours →" onClick={() => navigate('/ghost-hours')} last />
      </div>
    </div>
  );

  const accordionSection = (
    <div>
      {TOOLKIT_DATA.groups.map(g => (
        <AccordionGroup key={g.id} group={g}
          open={openGroup === g.id}
          onToggle={() => setOpenGroup(openGroup === g.id ? null : g.id)}
          onItemAction={handleItemAction} />
      ))}
    </div>
  );

  /* ── Desktop: grid crisis + two-column ── */
  if (isDesktop) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column',
        background: tokens.ground, color: tokens.textHi, fontFamily: tokens.fontBody, position: 'relative' }}>
        <div className="gl-atmosphere" />
        <Navbar />

        <main style={{ flex: 1, overflowY: 'auto', position: 'relative', zIndex: 2 }}>
          <div style={{ maxWidth: 1040, margin: '0 auto', padding: '40px 40px 80px' }}>
            <p style={{
              fontFamily: tokens.fontDisplay, fontStyle: 'italic',
              fontSize: 19, lineHeight: 1.5, color: tokens.textMid,
              margin: 0,
            }}>
              If you're in the weeds, start here. Everything in this kit was vetted by people who know the work. Use what helps, skip what doesn't.
            </p>

            <div style={{ marginTop: 32 }}>
              <DesktopCrisisGrid items={TOOLKIT_DATA.crisis} />
            </div>

            {/* Right Now — 3 cards side by side */}
            <div style={{ marginTop: 40 }}>
              <div style={{
                fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: tokens.accent, marginBottom: 14,
              }}>Right now</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <DesktopRightNowCard
                  label="I can't catch my breath"
                  sub="60-second guided breathing"
                  cta="Start breathing"
                  onClick={() => setExercise('breath')} />
                <DesktopRightNowCard
                  label="I need to come back to my body"
                  sub="5-4-3-2-1 grounding"
                  cta="Start grounding"
                  onClick={() => setExercise('ground')} />
                <DesktopRightNowCard
                  label="I can't sleep and I'm alone"
                  sub="Talk to someone who's up late too"
                  cta="Ghost Hours"
                  onClick={() => navigate('/ghost-hours')} />
              </div>
            </div>

            {isAdmin && (
              <div style={{ marginTop: 24 }}>
                {showAdminForm ? (
                  <AdminResourceForm onDone={() => setShowAdminForm(false)} />
                ) : (
                  <button className="gl-btn-bare" onClick={() => setShowAdminForm(true)} style={{
                    fontFamily: tokens.fontBody, fontSize: 12,
                    color: tokens.textLow, letterSpacing: '0.05em',
                    padding: '6px 12px', border: `1px solid ${tokens.edge}`,
                  }}>+ Add resource</button>
                )}
              </div>
            )}

            {/* Resource groups — accordion reveals card grid */}
            <div style={{ marginTop: 36 }}>
              {TOOLKIT_DATA.groups.map(g => (
                <DesktopResourceGroup key={g.id} group={g}
                  open={openGroup === g.id}
                  onToggle={() => setOpenGroup(openGroup === g.id ? null : g.id)}
                  onItemAction={handleItemAction} />
              ))}
            </div>

            <p style={{ marginTop: 40, fontFamily: tokens.fontBody, fontSize: 12, lineHeight: 1.6, color: tokens.textLow }}>
              We don't take a cut from any resource listed here.
            </p>
          </div>
        </main>

        {exercise === 'breath' && <BreathingModal tokens={tokens} onClose={() => setExercise(null)} />}
        {exercise === 'ground' && <GroundingModal tokens={tokens} onClose={() => setExercise(null)} />}
      </div>
    );
  }

  /* ── Mobile: single column with carousel ── */
  return (
    <div style={{ position: 'relative', minHeight: '100vh', paddingBottom: 40 }}>
      <Navbar />
      <div className="gl-atmosphere" />

      <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <TopBar tokens={tokens} title="THE TOOLKIT" />

      <div style={{ position: 'relative', zIndex: 2, padding: '8px 22px 0' }}>
        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 17, lineHeight: 1.5,
          color: tokens.textMid,
          margin: '14px 0 22px',
          letterSpacing: tokens.displayTracking,
        }}>
          If you're in the weeds, start here. Everything in this kit was vetted by people who know the work. Use what helps, skip what doesn't.
        </p>

        <CrisisRail tokens={tokens} items={TOOLKIT_DATA.crisis} />

        <div style={{ marginTop: 32 }}>
          {rightNowSection}
        </div>

        {isAdmin && (
          <div style={{ marginTop: 24 }}>
            {showAdminForm ? (
              <AdminResourceForm onDone={() => setShowAdminForm(false)} />
            ) : (
              <button className="gl-btn-bare" onClick={() => setShowAdminForm(true)} style={{
                fontFamily: tokens.fontBody, fontSize: 12,
                color: tokens.textLow, letterSpacing: '0.05em',
                padding: '6px 12px', border: `1px solid ${tokens.edge}`,
              }}>+ Add resource</button>
            )}
          </div>
        )}

        <div style={{ marginTop: 18 }}>{accordionSection}</div>

        <p style={{
          marginTop: 40, fontFamily: tokens.fontBody,
          fontSize: 12, lineHeight: 1.6, color: tokens.textLow,
        }}>We don't take a cut from any resource listed here.</p>
      </div>
      </div>

      {exercise === 'breath' && <BreathingModal tokens={tokens} onClose={() => setExercise(null)} />}
      {exercise === 'ground' && <GroundingModal tokens={tokens} onClose={() => setExercise(null)} />}
    </div>
  );
}
